import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Html5QrcodeScanner } from "html5-qrcode";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CardHeader,
  IconButton,
  Button,
  Box,
  TextField,
  Collapse,
  Divider,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Refresh,
  ExpandMore,
  ExpandLess,
  QrCodeScanner,
  QrCode2,
} from "@mui/icons-material";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BadgeIcon from "@mui/icons-material/Badge";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import QRCodeCard from "./QRCodeCard";
import {
  GetDashboardStats,
  GetTodayAttendance,
  AdminScanQR,
} from "../../../Services/Services";

const cardColors = [
  "linear-gradient(135deg, #6a11cb 0%, #8e54e9 100%)",
  "linear-gradient(135deg, #ff8008 0%, #ffb347 100%)",
  "linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)",
  "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
  "linear-gradient(135deg, #373B44 0%, #4286f4 100%)",
];

export default function ScannerAndQR() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const name = localStorage.getItem("name");
  const role = localStorage.getItem("role");
  const user_id = localStorage.getItem("id");

  const [dashboard, setDashboard] = useState({
    total: 0,
    present: 0,
    absent: 0,
    employees: 0,
  });
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [time, setTime] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState("");
  const [mobileTab, setMobileTab] = useState("scanner");

  // Time
  useEffect(() => {
    const update = () => setTime(new Date().toLocaleTimeString());
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, []);

  // API
  const loadDashboard = async () => {
    try {
      const data = await GetDashboardStats();
      setDashboard({
        total: data?.total || 0,
        present: data?.present || 0,
        absent: data?.absent || 0,
        employees: data?.employees || 0,
      });
    } catch (err) {
      console.error("Dashboard error:", err);
    }
  };

  const loadTodayAttendance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        role: role || "",
        user_id: user_id || "",
      });
      const data = await GetTodayAttendance(params.toString());

      // ✅ Always force array (fixes .map crash)
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.records)
        ? data.records
        : [];

      setTodayAttendance(list);
      setFilteredAttendance(list);
    } catch (err) {
      console.error("Attendance error:", err);
      setTodayAttendance([]);
      setFilteredAttendance([]);
      toast.error(err.response?.data?.message || "Failed to load attendance");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDashboard();
    loadTodayAttendance();
    const i = setInterval(() => {
      loadTodayAttendance();
      loadDashboard();
    }, 30000);
    return () => clearInterval(i);
  }, []);

  // Filter (safe)
  useEffect(() => {
    const t = setTimeout(() => {
      const safeList = Array.isArray(todayAttendance) ? todayAttendance : [];
      if (!search.trim()) {
        setFilteredAttendance(safeList);
        return;
      }
      const s = search.toLowerCase();
      setFilteredAttendance(
        safeList.filter(
          (r) =>
            r.user_name?.toLowerCase().includes(s) ||
            r.name?.toLowerCase().includes(s) ||
            r.unique_id?.toLowerCase().includes(s)
        )
      );
    }, 300);
    return () => clearTimeout(t);
  }, [search, todayAttendance]);

  // Scanner
  const handleScan = async (scannedData) => {
    setScanResult(null);
    setScanError("");

    let qrToken = scannedData.trim();
    if (qrToken.includes("token=")) {
      try {
        const url = new URL(qrToken);
        qrToken = url.searchParams.get("token") || qrToken;
      } catch {
        const match = qrToken.match(/token=([^&\s]+)/);
        if (match) qrToken = match[1];
      }
    }

    if (!navigator.geolocation) {
      setScanError("Geolocation not supported");
      toast.error("Geolocation not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const data = await AdminScanQR({
            qr_token: qrToken,
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
          setScanResult(data);
          toast.success(data.message || "Attendance marked!");
          loadTodayAttendance();
          loadDashboard();
        } catch (err) {
          const errMsg = err.response?.data?.error || "Invalid QR";
          setScanError(errMsg);
          toast.error(errMsg);
        }
      },
      () => {
        const errMsg = "Please allow location access";
        setScanError(errMsg);
        toast.error(errMsg);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (scanResult || scanError) return;
    const id = isMobile ? "admin-reader-mobile" : "admin-reader-desktop";
    if (isMobile && mobileTab !== "scanner") return;

    let scanner;
    const timer = setTimeout(() => {
      if (!document.getElementById(id)) return;
      scanner = new Html5QrcodeScanner(
        id,
        { fps: 10, qrbox: isMobile ? 200 : 220 },
        false
      );
      scanner.render(
        (text) => {
          scanner.clear();
          handleScan(text);
        },
        () => {}
      );
    }, 500);

    return () => {
      clearTimeout(timer);
      scanner?.clear().catch(() => {});
    };
  }, [isMobile, mobileTab, scanResult, scanError]);

  const convertTo12Hour = (t) => {
    if (!t || t === "--") return "-";
    // already 12-hour like "09:30 AM"
    if (String(t).toLowerCase().includes("am") || String(t).toLowerCase().includes("pm")) {
      return t;
    }
    const [h, m] = String(t).split(":");
    const hr = parseInt(h, 10);
    if (Number.isNaN(hr)) return t;
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`;
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  // Safe list for render
  const attendanceList = Array.isArray(filteredAttendance)
    ? filteredAttendance
    : [];

  const cardData = [
    {
      title: "Total Employees",
      icon: <PeopleIcon fontSize="large" />,
      value: dashboard.total || 0,
    },
    {
      title: "Present Today",
      icon: <CheckCircleIcon fontSize="large" />,
      value: dashboard.present || 0,
    },
    {
      title: "Absent Today",
      icon: <CancelIcon fontSize="large" />,
      value: dashboard.absent || 0,
    },
    {
      title: "Employees",
      icon: <BadgeIcon fontSize="large" />,
      value: dashboard.employees || 0,
    },
    {
      title: `Time - ${time}`,
      icon: <AccessTimeIcon fontSize="large" />,
      value: new Date().toLocaleDateString("en-IN", {
        weekday: "short",
        day: "2-digit",
        month: "short",
      }),
    },
  ];

  const MobileCard = ({ r, idx }) => {
    const isOpen = expandedCard === idx;
    const isPresent = r.status?.toLowerCase() === "present";
    return (
      <Box key={r.id || idx}>
        <Box
          onClick={() => setExpandedCard(isOpen ? null : idx)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.3,
            cursor: "pointer",
            bgcolor: idx % 2 === 0 ? "#fafafa" : "#fff",
            borderLeft: `4px solid ${isPresent ? "green" : "red"}`,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#666" }}>
              #{idx + 1} • {formatDate(r.date)} • {r.unique_id}
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
              {r.user_name || r.name || "-"}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#555" }}>
              {convertTo12Hour(r.in_time)} / {convertTo12Hour(r.out_time)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.5,
            }}
          >
            <Chip
              label={r.status || "-"}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                bgcolor: isPresent ? "green" : "red",
                color: "#fff",
              }}
            />
            <IconButton size="small" sx={{ p: 0 }}>
              {isOpen ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: idx % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: `4px solid ${isPresent ? "green" : "red"}`,
            }}
          >
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8 }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>
                  TOTAL TIME
                </Typography>
                <Typography sx={{ fontSize: "0.72rem" }}>
                  {r.total_time || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>
                  CONTACT
                </Typography>
                <Typography sx={{ fontSize: "0.72rem" }}>
                  {r.mobile || "-"}
                </Typography>
              </Box>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>
                  EMAIL
                </Typography>
                <Typography sx={{ fontSize: "0.72rem" }}>
                  {r.email || "-"}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider />
        </Collapse>
      </Box>
    );
  };

  return (
    <>
      <ToastContainer />
      <div style={{ padding: "5px" }}>
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mb: 2, borderLeft: "4px solid #1976d2", pl: 1.5 }}
        >
          Welcome, {name}
        </Typography>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {cardData.map((card, index) => (
            <Grid item xs={12} sm={6} md={2.4} key={index}>
              <Card
                sx={{
                  height: 100,
                  borderRadius: 0,
                  background: cardColors[index],
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  "&:hover": { transform: "translateY(-5px)" },
                  transition: "0.3s",
                }}
              >
                <div style={{ marginRight: 12 }}>{card.icon}</div>
                <CardContent sx={{ p: 0 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {card.title}
                  </Typography>
                  <Typography variant="h6">{card.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Scanner + QR */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {isMobile && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                <Button
                  fullWidth
                  size="small"
                  variant={mobileTab === "scanner" ? "contained" : "outlined"}
                  startIcon={<QrCodeScanner />}
                  onClick={() => setMobileTab("scanner")}
                >
                  Scanner
                </Button>
                <Button
                  fullWidth
                  size="small"
                  variant={mobileTab === "qr" ? "contained" : "outlined"}
                  startIcon={<QrCode2 />}
                  onClick={() => setMobileTab("qr")}
                >
                  QR
                </Button>
              </Box>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: isMobile && mobileTab !== "qr" ? "none" : "block" }}
          >
            <Card sx={{ boxShadow: 3, borderRadius: 0, height: "100%" }}>
              <CardHeader
                title="My QR Code"
                titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              />
              <CardContent sx={{ display: "flex", justifyContent: "center" }}>
                <QRCodeCard />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: isMobile && mobileTab !== "scanner" ? "none" : "block",
            }}
          >
            <Card sx={{ boxShadow: 3, borderRadius: 0, height: "100%" }}>
              <CardHeader
                title="Scan Employee QR"
                titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              />
              <CardContent sx={{ textAlign: "center" }}>
                {!scanResult && !scanError && (
                  <Box
                    id={
                      isMobile ? "admin-reader-mobile" : "admin-reader-desktop"
                    }
                  />
                )}
                {scanError && (
                  <Box>
                    <Typography color="error" variant="body2">
                      {scanError}
                    </Typography>
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      variant="contained"
                      color="error"
                      onClick={() => {
                        setScanError("");
                        setScanResult(null);
                      }}
                    >
                      Scan Again
                    </Button>
                  </Box>
                )}
                {scanResult && (
                  <Box>
                    <Typography fontWeight="bold">
                      {scanResult.message}
                    </Typography>
                    <Typography variant="body2">
                      {scanResult.user_name} • {scanResult.unique_id}
                    </Typography>
                    <Typography variant="body2">
                      In: {convertTo12Hour(scanResult.in_time)}{" "}
                      {scanResult.out_time &&
                        `| Out: ${convertTo12Hour(scanResult.out_time)}`}
                    </Typography>
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      variant="contained"
                      onClick={() => {
                        setScanResult(null);
                        setScanError("");
                      }}
                    >
                      Scan Next
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Today's Attendance */}
        <Card sx={{ boxShadow: 3, borderRadius: 0 }}>
          <CardHeader
            title={`Today's Attendance (${attendanceList.length})`}
            titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            action={
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Search name, ID"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ width: isMobile ? 120 : 200 }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    loadTodayAttendance();
                    loadDashboard();
                  }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
                {!isMobile && (
                  <Link
                    to="/dashboard/employee-attendance"
                    style={{ fontSize: "0.8rem" }}
                  >
                    View All
                  </Link>
                )}
              </Box>
            }
          />
          <CardContent sx={{ p: isMobile ? 0 : 2 }}>
            {loading ? (
              <Box textAlign="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : isMobile ? (
              <Box sx={{ bgcolor: "background.paper" }}>
                {attendanceList.length > 0 ? (
                  attendanceList
                    .slice(0, 50)
                    .map((r, i) => <MobileCard key={r.id || i} r={r} idx={i} />)
                ) : (
                  <Typography textAlign="center" p={3}>
                    No records
                  </Typography>
                )}
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{ borderRadius: 0, maxHeight: 400, overflowY: "auto" }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: "max-content",
                    "& td, & th": {
                      borderBottom: "1px solid #ccc",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableHead
                    sx={{
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell align="center">S.No.</TableCell>
                      <TableCell align="center">Date</TableCell>
                      <TableCell align="center">Name</TableCell>
                      <TableCell align="center">Employee ID</TableCell>
                      <TableCell align="center">In Time</TableCell>
                      <TableCell align="center">Out Time</TableCell>
                      <TableCell align="center">Total Time</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attendanceList.length > 0 ? (
                      attendanceList.map((r, idx) => (
                        <TableRow
                          key={r.id || idx}
                          sx={{
                            backgroundColor:
                              idx % 2 === 0 ? "#fafafa" : "#fff",
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                              "& td": { backgroundColor: "#e3f2fd" },
                            },
                          }}
                        >
                          <TableCell align="center">{idx + 1}</TableCell>
                          <TableCell align="center">
                            {formatDate(r.date)}
                          </TableCell>
                          <TableCell align="center">
                            {r.user_name || r.name || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {r.unique_id || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {convertTo12Hour(r.in_time)}
                          </TableCell>
                          <TableCell align="center">
                            {convertTo12Hour(r.out_time)}
                          </TableCell>
                          <TableCell align="center">
                            {r.total_time || "-"}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={r.status || "-"}
                              
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                bgcolor:
                                  r.status?.toLowerCase() === "present"
                                    ? "#80e100"
                                    : "red",
                                color: "#f9f9f9",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          No records
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}